import React from 'react';
import { Progress } from 'semantic-ui-react';
import CurrencyHelper from '../../helpers/currencyHelper';

interface SimpleProgressBarProps {
  /** Current value */
  value?: number;
  /** Target value (optional) */
  target?: number;
  /** Minimum target amount displayed as a milestone marker (optional) */
  minimumTargetAmount?: number;
  /** Currency code for formatting amounts (e.g., 'AUD', 'USD') */
  currency?: string;
  /** Label for the metric */
  label?: React.ReactNode;
  /** Color of the progress bar */
  color?: 'red' | 'orange' | 'yellow' | 'olive' | 'green' | 'teal' | 'blue' | 'violet' | 'purple' | 'pink' | 'brown' | 'grey' | 'black';
  /** Size of the progress bar */
  size?: 'tiny' | 'small' | 'medium' | 'large' | 'big';
  /** Show percentage text */
  showPercentage?: boolean;
  /** Custom display text to override the default value/target formatting */
  displayText?: string;
}

export const SimpleProgressBar: React.FC<SimpleProgressBarProps> = ({
  value = 0,
  target,
  minimumTargetAmount,
  currency,
  label,
  color = 'blue',
  size = 'medium',
  showPercentage = true,
  displayText
}) => {
  // Calculate percentage if target is provided
  const percentageRaw = target && target > 0 ? Math.min((value / target) * 100, 100) : 0;
  //format percentage to 2 decimal places, otherwise use whole numbers
  const percentage = percentageRaw.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });

  // Calculate milestone marker position
  const milestonePercentage = target && target > 0 && minimumTargetAmount
    ? Math.min((minimumTargetAmount / target) * 100, 100)
    : null;

  // Format an amount using CurrencyHelper when a currency is provided, otherwise plain number
  const fmt = (val: number) => {
    if (val === undefined || val === null) return '-';
    if (currency) return CurrencyHelper.formatCurrency(val, currency);
    return val.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  };
  const valueText = displayText || (target 
    ? `${fmt(value)} / ${fmt(target)}`
    : `${fmt(value)}`);

  const milestoneColor = 'var(--gr-darkest-grey)';

  return (
    <div style={{ marginBottom: '15px' }}>
      {label && (
        <div style={{ marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
          {label}
        </div>
      )}
      
      {target ? (
        // Show progress bar when target is available
        <div style={{ position: 'relative', marginTop: milestonePercentage !== null ? 28 : 0 }}>
          <Progress
            percent={percentage}
            total={target}
            progress={showPercentage ? 'percent' : false}
            color={color}
            size={size}
            style={{ margin: '0' }}
          />
          {milestonePercentage !== null && (
            <>
              {/* Milestone marker line */}
              <div
                style={{
                  position: 'absolute',
                  left: `${milestonePercentage}%`,
                  top: -6,
                  bottom: -6,
                  width: 3,
                  background: milestoneColor,
                  borderRadius: 2,
                  transform: 'translateX(-50%)',
                  transition: 'left 0.4s ease',
                  zIndex: 2,
                  pointerEvents: 'none'
                }}
              />
              {/* Milestone label */}
              <div
                style={{
                  position: 'absolute',
                  left: `${milestonePercentage}%`,
                  top: -28,
                  transform: 'translateX(-50%)',
                  fontSize: 12,
                  fontWeight: 600,
                  color: milestoneColor,
                  whiteSpace: 'nowrap',
                  transition: 'left 0.4s ease',
                  pointerEvents: 'none'
                }}
              >
                Min: {fmt(minimumTargetAmount!)}
              </div>
            </>
          )}
        </div>
      ) : (
        // Show just the value when no target
        <div style={{ 
          padding: '8px 12px', 
          backgroundColor: 'var(--background-tertiary)', 
          border: '1px solid var(--border-secondary)', 
          borderRadius: '4px',
          fontSize: '14px'
        }}>
          {valueText}
        </div>
      )}
      
      {target && (
        <div style={{ 
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: 10, 
          fontSize: 14, 
          color: '#666'
        }}>
          <span>{fmt(value)} raised</span>
          <span>Goal: {fmt(target)}</span>
        </div>
      )}
    </div>
  );
};

export default SimpleProgressBar;
